import {
  useState,
  useMemo,
  useDeferredValue,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import { ArrowUp } from 'lucide-react';
import { NodeStatusEvent, NodeEvent } from '@/types/workflow-types';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { Modal } from '@/components/modal';
import { MarkdownRenderer } from '@/components/markdown';
import { useNodeExplorerStore } from '@/stores/node-explorer-store';
import { useNodeStore } from '@/stores/node-store';
import {
  isNodeEvent,
  processStreamingEvents,
  getStableEventKey,
  getEventCategory,
} from './helpers/panel-event-helpers';
import { useEventsFilterStore } from '@/stores/events-filter-store';
import { EventsTimelineItem } from './events-timeline-item';
import { prefersReducedMotion } from './helpers/event-entry-animation';
import { useEnteringRows } from './helpers/use-entering-rows';

// Far enough that the pill never appears from a stray wheel nudge at the top.
const SCROLL_AWAY_PX = 24;

interface AwaySnapshot {
  listId: unknown;
  baseline: ReadonlySet<string>;
}

interface EventsTimelineProps {
  nodeStatuses: Record<string, NodeStatusEvent[]>;
  nodeId: string;
  header?: React.ReactNode;
  isConnected?: boolean;
  isPaused?: boolean;
}

type ContentType = 'text' | 'json' | 'markdown' | 'code';

export function EventsTimeline({
  nodeStatuses,
  nodeId,
  header,
  isConnected = false,
  isPaused = false,
}: EventsTimelineProps) {
  const [modalContent, setModalContent] = useState<{
    title: string;
    content: string;
    type: ContentType;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openChatTab = useNodeExplorerStore((s) => s.openChatTab);
  const selectNode = useNodeStore((s) => s.selectNode);

  const handleOpenChat = useCallback(
    (nodeEvent: NodeEvent) => {
      openChatTab();
      selectNode(nodeEvent.node_id);
    },
    [openChatTab, selectNode],
  );

  const nodeEvents = useMemo(() => {
    const entries = Object.entries(nodeStatuses);
    const matching: NodeStatusEvent[] = [];
    for (const [, events] of entries) {
      for (const event of events) {
        if (isNodeEvent(event) && event.node_id === nodeId) {
          matching.push(event);
        }
      }
    }
    return matching;
  }, [nodeStatuses, nodeId]);

  const chronologicalEvents = useMemo(
    () =>
      [...nodeEvents].sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      ),
    [nodeEvents],
  );

  const deferredChronological = useDeferredValue(chronologicalEvents);

  const processedEvents = useMemo(
    () => processStreamingEvents(deferredChronological).reverse(),
    [deferredChronological],
  );

  const enabledCategories = useEventsFilterStore((s) => s.enabled);

  const visibleEvents = useMemo(
    () => processedEvents.filter((e) => enabledCategories[getEventCategory(e)]),
    [processedEvents, enabledCategories],
  );

  const visibleKeys = useMemo(
    () => visibleEvents.map((e, i) => getStableEventKey(nodeId, e, i)),
    [visibleEvents, nodeId],
  );

  // Which list is on screen. A different node or a different filter is a
  // different list, so neither its rows nor its count carry over.
  const listId = useMemo(
    () => ({ nodeId, enabledCategories }),
    [nodeId, enabledCategories],
  );

  const enteringKeys = useEnteringRows(visibleKeys, listId);

  const scrollerRef = useRef<HTMLDivElement>(null);
  // What was on screen when the reader scrolled away, and which list it was.
  // Holding it as state rather than a ref keeps the count correct in the very
  // render a filter changes, instead of one render later.
  const [away, setAway] = useState<AwaySnapshot | null>(null);
  const isAwayRef = useRef(false);
  const currentRef = useRef({ keys: visibleKeys, listId });

  useLayoutEffect(() => {
    currentRef.current = { keys: visibleKeys, listId };
  });

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollTop = 0;
    isAwayRef.current = false;
    setAway(null);
  }, [nodeId]);

  // A filter change leaves the reader where they are, but the rows it reveals
  // are not arrivals, so the snapshot rebases onto the list now on screen.
  if (away && !Object.is(away.listId, listId)) {
    setAway({ listId, baseline: new Set(visibleKeys) });
  }

  const handleScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const isAway = scroller.scrollTop > SCROLL_AWAY_PX;
    if (isAway === isAwayRef.current) return;
    isAwayRef.current = isAway;
    setAway(
      isAway
        ? {
            listId: currentRef.current.listId,
            baseline: new Set(currentRef.current.keys),
          }
        : null,
    );
  }, []);

  const handleJumpToTop = useCallback(() => {
    scrollerRef.current?.scrollTo({
      top: 0,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }, []);

  const newEventCount =
    away && Object.is(away.listId, listId)
      ? visibleKeys.filter((key) => !away.baseline.has(key)).length
      : 0;

  const handleOpenModal = useCallback(
    (title: string, content: string, type: ContentType) => {
      setModalContent({ title, content, type });
      setIsModalOpen(true);
    },
    [],
  );

  const renderModalBody = () => {
    if (!modalContent) return null;
    switch (modalContent.type) {
      case 'markdown':
        return <MarkdownRenderer content={modalContent.content} />;
      case 'json':
      case 'code':
        return (
          <pre className='text-sm font-mono whitespace-pre-wrap break-all bg-muted p-4 rounded'>
            {modalContent.content}
          </pre>
        );
      default:
        return (
          <div className='text-sm whitespace-pre-wrap break-all font-mono'>
            {modalContent.content}
          </div>
        );
    }
  };

  return (
    <div className='flex-1 flex flex-col min-h-0'>
      <div className='relative flex-1 min-h-0'>
        {newEventCount > 0 && (
          <div className='pointer-events-none absolute inset-x-0 top-2.5 z-10 flex justify-center'>
            <Button
              size='sm'
              onClick={handleJumpToTop}
              className='pointer-events-auto h-7 gap-1.5 rounded-full px-3 text-xs shadow-lg animate-in fade-in slide-in-from-top-2 duration-200 motion-reduce:animate-none'
            >
              <ArrowUp className='size-3' />
              {newEventCount === 1
                ? '1 new event'
                : `${newEventCount} new events`}
            </Button>
          </div>
        )}
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className='h-full overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [scrollbar-width:thin]'
        >
          {header}
          <div className='flex flex-col'>
            {visibleEvents.length === 0 ? (
              <div className='flex items-center justify-center py-12 text-sm text-muted-foreground'>
                {processedEvents.length === 0
                  ? 'No events received'
                  : 'No events match the current filter'}
              </div>
            ) : (
              visibleEvents.map((event, index) => (
                <EventsTimelineItem
                  key={visibleKeys[index]}
                  event={event}
                  onOpenModal={handleOpenModal}
                  onOpenChat={handleOpenChat}
                  isConnected={isConnected}
                  isPaused={isPaused}
                  isEntering={enteringKeys.has(visibleKeys[index])}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <Modal
        isDialogOpen={isModalOpen}
        setIsDialogOpen={setIsModalOpen}
        title={
          <div className='flex items-center justify-between w-full'>
            <span>{modalContent?.title || 'Content'}</span>
            {modalContent && (
              <CopyButton text={modalContent.content} size='default' />
            )}
          </div>
        }
        contentClassName='max-w-4xl'
      >
        {renderModalBody()}
      </Modal>
    </div>
  );
}
